'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { buildSummarizeLinks, canonicalPageUrl, type AiSettings } from '@/lib/ai-summarize';
import { SUMMARIZE_LABEL } from '@/lib/ai-providers';
import { AI_ICONS } from '@/components/ai-icons';

// Same shape as ShareLinks / ThankYouTracking — identical optional members merge cleanly.
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export interface SummarizeWithAiProps {
  /** Resolved Site Settings `ai` group (see resolveAiSettings). */
  settings: AiSettings;
}

/**
 * GA4 custom `ai_summarize` event — which assistant visitors hand the page to,
 * and which page. Mirrors ShareLinks' `share` event (method / content_type /
 * item_id) so the two report side by side; page_location is auto-collected by
 * GA4, so the page goes in item_id rather than being sent twice.
 */
export function trackSummarize(provider: string, pageUrl: string): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'ai_summarize', { method: provider, content_type: 'page', item_id: pageUrl });
  }
}

// flex-wrap + max-w-full: on 320–360px phones the label plus five 32px icons is
// wider than the footer column, so the icons drop under the label instead of
// pushing the pill past the viewport edge (WCAG 1.4.10 reflow).
const PILL =
  'inline-flex max-w-full flex-wrap items-center justify-center gap-3 rounded-full ' +
  'border border-white/10 bg-white/[0.03] px-4 py-2';

// The global focus outline is ~1.4:1 against the dark footer, so each link
// draws its own 2px white ring (WCAG 2.4.7), offset so it clears the icon.
const LINK =
  'flex h-8 w-8 items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 ' +
  'focus-visible:text-white focus-visible:bg-white/10 transition-colors ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

/**
 * Footer row "Summarize with AI": one icon link per enabled assistant, each
 * opening the assistant with a prompt that carries this page's canonical URL.
 *
 * The URL comes from siteUrl + usePathname (never window.location) so it is
 * the canonical host even behind a proxy or preview hostname. `{title}` is
 * filled from document.title only after mount: the server render — and the
 * first client render — use '' so the two match and hydration never differs.
 */
export default function SummarizeWithAi({ settings }: SummarizeWithAiProps) {
  const pathname = usePathname() ?? '/';
  const url = canonicalPageUrl(settings.siteUrl, pathname);
  const [title, setTitle] = useState('');
  const [hovered, setHovered] = useState<string | null>(null);

  // Deliberate post-hydration read of a browser-only value: the server has no
  // document.title, and reading it during render would make the first client
  // render differ from the server markup. Running after commit also means
  // that, on a client-side navigation, the new page's <title> has already
  // landed by the time we read it. One extra render, no cascade.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTitle(document.title);
  }, [pathname]);

  if (!settings.showSummarize) return null;

  const links = buildSummarizeLinks({ url, title }, { template: settings.promptTemplate, providers: settings.providers });
  if (links.length === 0) return null;

  return (
    <div data-testid="summarize-with-ai" className={PILL}>
      {/*
        Two spans stacked in one grid cell. The invisible one always holds the
        default label — the longest text this slot ever shows — so the cell is
        exactly that wide and the icons never shift as the visible text swaps
        to a (shorter) provider name. A fixed min-width did the same job but
        could not shrink, which is what overflowed narrow phones; a
        content-sized reservation has no such floor.
      */}
      <span className="grid text-sm text-white/60">
        <span className="invisible col-start-1 row-start-1" aria-hidden="true">
          {SUMMARIZE_LABEL}
        </span>
        <span data-testid="summarize-label" className="col-start-1 row-start-1 text-right">
          {hovered ?? SUMMARIZE_LABEL}
        </span>
      </span>
      <ul className="flex items-center gap-1" aria-label="Summarize this page with an AI assistant">
        {links.map((l) => (
          <li key={l.id}>
            {/*
              The new-tab warning lives in the accessible name (WCAG G201). No
              title: beside an aria-label it is announced again as a
              description, and its tooltip duplicates the hover label above.
            */}
            <a
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Summarize this page with ${l.label} (opens in a new tab)`}
              data-provider={l.id}
              className={LINK}
              onMouseEnter={() => setHovered(l.label)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(l.label)}
              onBlur={() => setHovered(null)}
              onClick={() => trackSummarize(l.id, url)}
            >
              {AI_ICONS[l.id]}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
